import React, { createContext, useContext, useState, useEffect } from 'react';
import { useGetCompanyInfo } from '../services/companyService';
import { useGetPlanInfo } from '../services/planService';
import { useGetCustomerRoles } from '../services/customerRolesService';
import { extractPayrollDataFromToken } from '../services/authService';
import { getAccessToken } from '../utils';

const UserContext = createContext();

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [userRole, setUserRole] = useState(null);
  const [userBranch, setUserBranch] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [user, setUser] = useState(null);
  const [userPayrollRoles, setUserPayrollRoles] = useState([]);
  const [userPayrollPermissions, setUserPayrollPermissions] = useState([]);
  const [customerRoles, setCustomerRoles] = useState(null);
  const [employeeLimits, setEmployeeLimits] = useState({
    current: 0,
    limit: 0,
    remaining: 0
  });
  const [isOnDashboard, setIsOnDashboard] = useState(false);

  // Only fetch company info if user is authenticated
  const isAuthenticated = !!localStorage.getItem('access_token');
  

  const { companyInfo: companyData, companyInfoIsLoading } = useGetCompanyInfo({
    enabled: isAuthenticated
  });
  
  // Fetch plan info only when authenticated and on dashboard
  const shouldFetchPlan = isAuthenticated && isOnDashboard;
  const { planInfo: planData, planInfoIsLoading } = useGetPlanInfo({
    enabled: shouldFetchPlan
  });

  // Fetch customer roles when authenticated
  const { customerRoles: customerRolesData, customerRolesIsLoading } = useGetCustomerRoles({
    enabled: isAuthenticated
  });







  // Update company info when data is fetched
  useEffect(() => {
    if (companyData && !companyData.error) {
      setCompanyInfo(companyData);
    }
  }, [companyData]);

  // Update employee limits when plan data is fetched
  useEffect(() => {
    if (planData && !planData.error) {
      const plan = planData.data || planData;
      setEmployeeLimits({
        current: plan.current_employees || 0,
        limit: plan.max_employees || 0,
        remaining: plan.remaining_employees || 0
      });
    }
  }, [planData]);

  // Update customer roles when data is fetched
  useEffect(() => {
    if (customerRolesData && !customerRolesData.error) {
      setCustomerRoles(customerRolesData);
    }
  }, [customerRolesData]);



           // Determine user role from localStorage or API response
         useEffect(() => {
           const storedRole = localStorage.getItem('userRole');
           const storedBranch = localStorage.getItem('userBranch');
           const storedCompany = localStorage.getItem('userCompany');
           const storedPayrollRoles = localStorage.getItem('userPayrollRoles');
           const storedPayrollPermissions = localStorage.getItem('userPayrollPermissions');

           if (storedRole) {
             setUserRole(storedRole);
           }

           if (storedBranch) {
             try {
               setUserBranch(JSON.parse(storedBranch));
             } catch (error) {
               console.error('Error parsing stored branch:', error);
               localStorage.removeItem('userBranch');
             }
           }

           if (storedCompany) {
             try {
               const companyData = JSON.parse(storedCompany);
               // Update company info if we have stored company data
               if (companyData && !companyInfo) {
                 setCompanyInfo({
                   ...companyData,
                   employee_count: 0,
                   employee_limit: 10 // Default limit
                 });
               }
             } catch (error) {
               console.error('Error parsing stored company:', error);
               localStorage.removeItem('userCompany');
             }
           }

           // Parse payroll roles - fallback to token if localStorage is empty
           let payrollRoles = [];
           if (storedPayrollRoles) {
             try {
               payrollRoles = JSON.parse(storedPayrollRoles);
               setUserPayrollRoles(payrollRoles);
             } catch (error) {
               console.error('Error parsing stored payroll roles:', error);
               localStorage.removeItem('userPayrollRoles');
             }
           } else {
             // Fallback: Extract from token if localStorage is empty
             // Only try if we have a valid token string
             try {
               const token = getAccessToken();
               // Check if token exists and is a string (not an empty object)
               if (token && typeof token === 'string' && token.trim().length > 0) {
                 const tokenData = extractPayrollDataFromToken(token);
                 if (tokenData.payroll_roles && tokenData.payroll_roles.length > 0) {
                   payrollRoles = tokenData.payroll_roles;
                   setUserPayrollRoles(payrollRoles);
                   // Store in localStorage for future use
                   localStorage.setItem('userPayrollRoles', JSON.stringify(payrollRoles));
                 }
               }
             } catch (error) {
               // Silently fail - this is expected before login
             }
           }

           // Parse payroll permissions - fallback to token if localStorage is empty
           let payrollPermissions = [];
           if (storedPayrollPermissions) {
             try {
               payrollPermissions = JSON.parse(storedPayrollPermissions);
               setUserPayrollPermissions(payrollPermissions);
             } catch (error) {
               console.error('Error parsing stored payroll permissions:', error);
               localStorage.removeItem('userPayrollPermissions');
             }
           } else {
             // Fallback: Extract from token if localStorage is empty
             // Only try if we have a valid token string
             try {
               const token = getAccessToken();
               // Check if token exists and is a string (not an empty object)
               if (token && typeof token === 'string' && token.trim().length > 0) {
                 const tokenData = extractPayrollDataFromToken(token);
                 if (tokenData.payroll_permissions && tokenData.payroll_permissions.length > 0) {
                   payrollPermissions = tokenData.payroll_permissions;
                   setUserPayrollPermissions(payrollPermissions);
                   // Store in localStorage for future use
                   localStorage.setItem('userPayrollPermissions', JSON.stringify(payrollPermissions));
                 }
               }
             } catch (error) {
               // Silently fail - this is expected before login
             }
           }

           // Create user object from stored data
           if (storedRole) {
             const userObj = {
               role: storedRole,
               branch_id: storedBranch ? JSON.parse(storedBranch).id : null,
               branch_name: storedBranch ? JSON.parse(storedBranch).name : null,
               company_id: storedCompany ? JSON.parse(storedCompany).id : null,
               company_name: storedCompany ? JSON.parse(storedCompany).name : null,
               payroll_roles: payrollRoles,
               payroll_permissions: payrollPermissions
             };
             setUser(userObj);
           }
         }, []); // Run only on mount

  // Get the effective user role (prioritize customer_role from API over system role)
  const getEffectiveUserRole = () => {
    // If we have customer role data, use that as the primary role
    if (customerRoles?.customer_role) {
      return customerRoles.customer_role;
    }
    // Fallback to system role from localStorage
    return userRole;
  };

  // Check if user is admin (using effective role)
  const isAdmin = () => {
    const effectiveRole = getEffectiveUserRole();
    return effectiveRole === 'admin' || effectiveRole === 'company_admin' || effectiveRole === 'PAYROLL_ADMIN';
  };

  // Check if user can access admin features
  const canAccessAdminFeatures = () => {
    return isAdmin();
  };

  // Check if user can manage branches
  const canManageBranches = () => {
    return isAdmin();
  };

  // Check if user can manage users
  const canManageUsers = () => {
    return isAdmin();
  };

  // Check if user can view cross-branch data
  const canViewCrossBranchData = () => {
    return isAdmin();
  };

  // Check if employee limit is reached
  const isEmployeeLimitReached = employeeLimits.remaining <= 0;

  // Enhanced Payroll Access Control Functions using Customer Roles
  const isMainBranchUser = () => {
    return userBranch?.name === 'Main Branch' || userBranch?.name === 'main_branch';
  };

  const canAccessPayroll = () => {
    // Only Main Branch users can access payroll
    return isMainBranchUser();
  };

  const hasPayrollRole = (role) => {
    return userPayrollRoles.includes(role);
  };

  const hasPayrollPermission = (permission) => {
    return userPayrollPermissions.includes(permission);
  };

  // Enhanced role detection using customer_roles endpoint
  const getCustomerRole = () => {
    return customerRoles?.customer_role || null;
  };

  const isPayrollAdmin = () => {
    const effectiveRole = getEffectiveUserRole();
    return effectiveRole === 'company_admin' || 
           effectiveRole === 'PAYROLL_ADMIN' ||
           hasPayrollRole('PAYROLL_ADMIN') || 
           getCustomerRole() === 'PAYROLL_ADMIN';
  };

  const isHRManager = () => {
    const effectiveRole = getEffectiveUserRole();
    return effectiveRole === 'HR_MANAGER' ||
           hasPayrollRole('HR_MANAGER') || 
           getCustomerRole() === 'HR_MANAGER';
  };

  const isAccountant = () => {
    const effectiveRole = getEffectiveUserRole();
    return effectiveRole === 'ACCOUNTANT' ||
           hasPayrollRole('ACCOUNTANT') || 
           getCustomerRole() === 'ACCOUNTANT';
  };

  const isPayrollViewer = () => {
    const effectiveRole = getEffectiveUserRole();
    return effectiveRole === 'PAYROLL_VIEWER' ||
           hasPayrollRole('PAYROLL_VIEWER') || 
           getCustomerRole() === 'PAYROLL_VIEWER';
  };

  // Get current branch info
  const getCurrentBranch = () => {
    if (isAdmin() && selectedBranch) {
      return selectedBranch;
    }
    return userBranch;
  };

           // Update user role and branch
         const updateUserInfo = (role, branch = null, company = null) => {
           setUserRole(role);
           setUserBranch(branch);
           localStorage.setItem('userRole', role);
           if (branch) {
             localStorage.setItem('userBranch', JSON.stringify(branch));
           }
           if (company) {
             localStorage.setItem('userCompany', JSON.stringify(company));
           }
         };

         // Refresh user info from localStorage (useful after login)
         const refreshUserInfo = () => {
           const storedRole = localStorage.getItem('userRole');
           const storedBranch = localStorage.getItem('userBranch');
           const storedCompany = localStorage.getItem('userCompany');
           const storedPayrollRoles = localStorage.getItem('userPayrollRoles');
           const storedPayrollPermissions = localStorage.getItem('userPayrollPermissions');

           if (storedRole) {
             setUserRole(storedRole);
           }

           if (storedBranch) {
             try {
               setUserBranch(JSON.parse(storedBranch));
             } catch (error) {
               console.error('Error parsing stored branch:', error);
               localStorage.removeItem('userBranch');
             }
           }

           if (storedPayrollRoles) {
             try {
               const payrollRoles = JSON.parse(storedPayrollRoles);
               setUserPayrollRoles(payrollRoles);
             } catch (error) {
               console.error('Error parsing stored payroll roles:', error);
               localStorage.removeItem('userPayrollRoles');
             }
           }

           if (storedPayrollPermissions) {
             try {
               const payrollPermissions = JSON.parse(storedPayrollPermissions);
               setUserPayrollPermissions(payrollPermissions);
             } catch (error) {
               console.error('Error parsing stored payroll permissions:', error);
               localStorage.removeItem('userPayrollPermissions');
             }
           }

           if (storedCompany) {
             try {
               const companyData = JSON.parse(storedCompany);
               if (companyData && !companyInfo) {
                 setCompanyInfo({
                   ...companyData,
                   employee_count: 0,
                   employee_limit: 10 // Default limit
                 });
               }
             } catch (error) {
               console.error('Error parsing stored company:', error);
               localStorage.removeItem('userCompany');
             }
           }

           // Create user object from stored data
           if (storedRole) {
             const userObj = {
               role: storedRole,
               branch_id: storedBranch ? JSON.parse(storedBranch).id : null,
               branch_name: storedBranch ? JSON.parse(storedBranch).name : null,
               company_id: storedCompany ? JSON.parse(storedCompany).id : null,
               company_name: storedCompany ? JSON.parse(storedCompany).name : null
             };
             setUser(userObj);
             
             // Debug logging
         
           }

           // Trigger dashboard refresh after login
           // This will cause the dashboard to refetch all data
           setTimeout(() => {
             // Force a re-render by updating dashboard state
             setIsOnDashboard(false);
             setTimeout(() => {
               setIsOnDashboard(true);
             }, 100);
           }, 500);
         };

  // Update selected branch (for admin filtering)
  const updateSelectedBranch = (branch) => {
    setSelectedBranch(branch);
  };

  // Set dashboard state (called from dashboard component)
  const setDashboardState = (isOnDashboardPage) => {
    setIsOnDashboard(isOnDashboardPage);
  };

           // Clear user info (for logout)
         const clearUserInfo = () => {
           setUser(null);
           setUserRole(null);
           setUserBranch(null);
           setSelectedBranch(null);
           setCompanyInfo(null);
           setUserPayrollRoles([]);
           setUserPayrollPermissions([]);
           setEmployeeLimits({ current: 0, limit: 0, remaining: 0 });
           localStorage.removeItem('userRole');
           localStorage.removeItem('systemRole');
           localStorage.removeItem('userBranch');
           localStorage.removeItem('userCompany');
           localStorage.removeItem('userPayrollRoles');
           localStorage.removeItem('userPayrollPermissions');
         };

                       const value = {
            // User info
            user,
            userRole,
            userBranch,
            userPayrollRoles,
            userPayrollPermissions,
            customerRoles,
            companyInfo,
            selectedBranch,
            employeeLimits,
            planInfo: planData,
            planInfoIsLoading,
           
           // Permissions
           isAdmin,
           canAccessAdminFeatures,
           canManageBranches,
           canManageUsers,
           canViewCrossBranchData,
           isEmployeeLimitReached,
           
           // Enhanced Payroll Access Control
           canAccessPayroll,
           isMainBranchUser,
           hasPayrollRole,
           hasPayrollPermission,
           getCustomerRole,
           getEffectiveUserRole,
           isPayrollAdmin,
           isHRManager,
           isAccountant,
           isPayrollViewer,
           
                       // Methods
            updateUserInfo,
            updateSelectedBranch,
            setDashboardState,
            clearUserInfo,
            getCurrentBranch,
            refreshUserInfo,
           
                       // Loading state
            companyInfoIsLoading,
            customerRolesIsLoading
         };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}; 